// Zustand store for the auth slice — reads its ports from the
// composition root in `src/container.ts` (UI never imports
// `@/adapters/*` directly, per the ESLint boundary rule).
//
// Pattern locked by FE PR #669 G6 audit (memory 2026-05-25):
//   1. every user-scoped store exposes `reset()`
//   2. logout calls `reset()` on EVERY store (cross-user leak prevention)
//
// Today the add-in only has this single auth store. As PR-2b / PR-3 land
// (case picker store, citation registry, …), each new store registers
// itself here under the comment "// add: useFooStore.getState().reset()".

import { create } from 'zustand'
import { getContainer, setContainer } from '@/container'
import { useCaseStore } from '@/ui/stores/use-case-store'
import { useTabStore } from '@/ui/stores/use-tab-store'
import { useAgentStore } from '@/ui/stores/use-agent-store'
import { signIn, signOut } from '@/domain/auth/usecases/sign-in'
import type { AuthSession, Credentials } from '@/domain/auth/entity'
import type { AuthPort, AuthStoragePort } from '@/domain/auth/ports'

/** Test seam — overrides adapters at the container level. React
 *  components only ever talk to `useAuthStore`, so swapping ports here
 *  is enough. */
export function setAuthDeps(deps: {
  auth?: AuthPort
  storage?: AuthStoragePort
}): void {
  setContainer({
    ...(deps.auth ? { authHttp: deps.auth } : {}),
    ...(deps.storage ? { authStorage: deps.storage } : {}),
  })
}

interface AuthState {
  /** Session is null until `initialize()` (re-hydrate F5) or `login()` resolves. */
  session: AuthSession | null
  isAuthenticated: boolean
  isInitializing: boolean
  isSubmitting: boolean
  /** Last error message surfaced to the LoginScreen. Cleared on next submit. */
  errorMessage: string | null
  /** Re-hydrate from localStorage on mount. Called once from `App`. */
  initialize: () => void
  login: (credentials: Credentials) => Promise<void>
  logout: () => Promise<void>
  /** W5 invariant — exposed for callers that need to nuke the store
   *  outside the logout flow (e.g. a 401 burst from a stale token). */
  reset: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  session: null,
  isAuthenticated: false,
  isInitializing: true,
  isSubmitting: false,
  errorMessage: null,
  initialize: () => {
    const existing = getContainer().authStorage.read()
    set({
      session: existing,
      isAuthenticated: existing !== null,
      isInitializing: false,
    })
  },
  login: async (credentials: Credentials) => {
    set({ isSubmitting: true, errorMessage: null })
    try {
      const c = getContainer()
      const session = await signIn(
        { auth: c.authHttp, storage: c.authStorage },
        credentials,
      )
      set({
        session,
        isAuthenticated: true,
        isSubmitting: false,
        errorMessage: null,
      })
    } catch (err) {
      set({
        isSubmitting: false,
        errorMessage:
          err instanceof Error
            ? err.message
            : 'Connexion impossible, réessayez.',
      })
      throw err
    }
  },
  logout: async () => {
    const c = getContainer()
    await signOut({ auth: c.authHttp, storage: c.authStorage })
    // W5 — reset every user-scoped store on logout (cross-user leak).
    // Add a one-line comment per store to make the invariant impossible
    // to miss.
    useCaseStore.getState().reset()
    useTabStore.getState().reset() // W5 — back to Sources on logout (F-SHELL)
    useAgentStore.getState().reset() // W5 — clear chat thread on logout (F-AGENT)
    // add: useCitationStore.getState().resetAll()  // PR-3
    set({
      session: null,
      isAuthenticated: false,
      isSubmitting: false,
      errorMessage: null,
    })
  },
  reset: () => {
    getContainer().authStorage.clear()
    set({
      session: null,
      isAuthenticated: false,
      isSubmitting: false,
      errorMessage: null,
    })
  },
}))
